import React, { useEffect, useRef } from 'react';
import { createClient } from 'contentful-management';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import ReactGA4 from 'react-ga4';
import { isbot } from 'isbot';

// Constants
const PRODUCTION_DOMAIN = 'mohij.com';
const GA_MEASUREMENT_ID = 'G-S868L1LSPX';
const CONTENTFUL_SPACE_ID = '7lqh82unq55r';
const HUMAN_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];

// Resolves on the visitor's first genuine interaction while the tab is visible.
// Crawlers, link-preview bots and uptime checkers load the page but never interact.
const FORWARDED_EVENT = 'visitor-human-interaction';
const waitForHuman = (signal) => new Promise((resolve) => {
    const start = performance.now();
    const onEvent = (e) => {
        const forwarded = e.type === FORWARDED_EVENT;
        if ((!forwarded && !e.isTrusted) || document.visibilityState !== 'visible') return;
        HUMAN_EVENTS.concat(FORWARDED_EVENT).forEach((type) => window.removeEventListener(type, onEvent, true));
        resolve({ interaction: forwarded ? e.detail : e.type, engagedAfterMs: Math.round(performance.now() - start) });
    };
    HUMAN_EVENTS.concat(FORWARDED_EVENT).forEach((type) => window.addEventListener(type, onEvent, { capture: true, passive: true, signal }));
    signal.addEventListener('abort', () => resolve(null));
});

// Same-origin iframes (the Clientele spiral) swallow input events, so they forward
// their genuine interactions to the main window.
export const forwardHumanEvents = (frameWindow) => {
    const onEvent = (e) => {
        if (!e.isTrusted) return;
        window.dispatchEvent(new CustomEvent(FORWARDED_EVENT, { detail: e.type }));
    };
    HUMAN_EVENTS.forEach((type) => frameWindow.addEventListener(type, onEvent, { capture: true, passive: true, once: true }));
};

// Detects automated browsers (Puppeteer, Playwright, Selenium…) that send a normal user agent.
const detectAutomation = async () => {
    try {
        const { load } = await import('@fingerprintjs/botd');
        const botd = await load({ monitoring: false });
        return botd.detect().bot ? 'bot' : 'human';
    } catch {
        return 'unknown';
    }
};

// Initialize Contentful client
const managementClient = createClient({
    accessToken: process.env.REACT_APP_CONTENTFUL_MANAGEMENT_TOKEN || 'CFPAT-0mpIGgPEN-Ir4z8LpMbTwdcEcL3rXLA4mLfbgJPE548'
});

// "Asia/Dubai" → "Dubai"
const placeFromTimezone = (timezone) =>
    (timezone || '').split('/').pop().replace(/_/g, ' ') || 'Unknown place';

const referrerHost = (referrer) => {
    try {
        const host = new URL(referrer).hostname.replace(/^www\./, '');
        return host.endsWith(PRODUCTION_DOMAIN) ? 'direct' : host;
    } catch {
        return 'direct';
    }
};

const getUtm = () => {
    const params = new URLSearchParams(window.location.search);
    return ['utm_source', 'utm_medium', 'utm_campaign']
        .map((key) => params.get(key))
        .filter(Boolean)
        .join(' / ');
};

const VisitorTracker = ({ debug = false }) => {
    const updateInProgress = useRef(false);
    const mounted = useRef(false);
    const gaInitialized = useRef(false);
    const humanWait = useRef(null);

    // Check if tracking should be enabled
    const isTrackingEnabled = () => {
        const hostname = window.location.hostname;
        const isProductionDomain = hostname === PRODUCTION_DOMAIN || hostname === `www.${PRODUCTION_DOMAIN}`;
        const isTrackingFlagEnabled = process.env.REACT_APP_ENABLE_TRACKING === 'true';
        
        if (debug) {
            console.log('Tracking enabled:', isTrackingFlagEnabled || isProductionDomain);
        }
        return isProductionDomain || isTrackingFlagEnabled;
    };

    // Get or create visitor ID
    const getOrCreateVisitorId = () => {
        let visitorId = localStorage.getItem('visitor_id');
        if (!visitorId) {
            visitorId = uuidv4();
            localStorage.setItem('visitor_id', visitorId);
            if (debug) {
                console.log('New visitor ID created:', visitorId);
            }
        }
        return visitorId;
    };

    // Get enhanced device information
    const getEnhancedDeviceInfo = () => {
        const parser = new UAParser();
        const result = parser.getResult();

        return {
            deviceInfo: {
                type: result.device.type || 'Desktop',
                model: result.device.model || 'Unknown',
                vendor: result.device.vendor || 'Unknown',
            },
            browserInfo: {
                name: result.browser.name || 'Unknown',
                version: result.browser.version || 'Unknown',
                language: navigator.language,
                cookiesEnabled: navigator.cookieEnabled,
                userAgent: navigator.userAgent,
            },
            osInfo: {
                name: result.os.name || 'Unknown',
                version: result.os.version || 'Unknown',
            },
            displayInfo: {
                screenSize: `${window.screen.width}x${window.screen.height}`,
                viewportSize: `${window.innerWidth}x${window.innerHeight}`,
                colorDepth: window.screen.colorDepth,
                pixelRatio: window.devicePixelRatio || 1,
            }
        };
    };

    // Initialize Google Analytics
    const initializeGA = () => {
        if (!gaInitialized.current) {
            ReactGA4.initialize(GA_MEASUREMENT_ID, {
                debug: process.env.NODE_ENV === 'development' || debug,
                testMode: process.env.NODE_ENV === 'development',
                gaOptions: {
                    cookieFlags: 'SameSite=None;Secure'
                }
            });
            gaInitialized.current = true;
            if (debug) {
                console.log('GA4 initialized with measurement ID:', GA_MEASUREMENT_ID);
            }
        }
    };

    // Send events to Google Analytics
    const sendGAEvents = (visitorId, deviceData) => {
        // Set user ID and default parameters
        ReactGA4.set({
            userId: visitorId,
            user_properties: {
                visitor_type: 'website_visitor',
                first_visit_date: localStorage.getItem('first_visit_date') || new Date().toISOString()
            }
        });

        // Send pageview
        ReactGA4.send({
            hitType: "pageview",
            page: window.location.pathname,
            title: document.title,
            userId: visitorId
        });

        // Send visitor data event
        const visitorEvent = {
            visitor_id: visitorId,
            device_type: deviceData.deviceInfo.type,
            device_model: deviceData.deviceInfo.model,
            device_vendor: deviceData.deviceInfo.vendor,
            browser_name: deviceData.browserInfo.name,
            browser_version: deviceData.browserInfo.version,
            browser_language: deviceData.browserInfo.language,
            os_name: deviceData.osInfo.name,
            os_version: deviceData.osInfo.version,
            screen_size: deviceData.displayInfo.screenSize,
            viewport_size: deviceData.displayInfo.viewportSize,
            referrer: document.referrer || 'Direct',
            non_interaction: true
        };

        ReactGA4.event('visitor_data', visitorEvent);

        if (debug) {
            console.log('GA4 event sent:', visitorEvent);
        }
    };

    // One readable line for the Slack alert. Contentful's Slack app shows the entry title,
    // and the visitor type's title field is visitorId, so the summary goes there.
    const buildSummary = (visitorId, deviceData, human, landing, timezone) => {
        const { deviceInfo, osInfo, browserInfo } = deviceData;
        const device = [
            deviceInfo.type === 'Desktop' ? 'Desktop' : `${deviceInfo.vendor} ${deviceInfo.model}`.replace(/Unknown ?/g, '').trim() || deviceInfo.type,
            [osInfo.name, osInfo.version].filter((v) => v && v !== 'Unknown').join(' '),
            browserInfo.name,
        ].filter((v) => v && v !== 'Unknown').join(', ');
        const from = referrerHost(landing.referrer);

        return [
            `👤 ${placeFromTimezone(timezone)}`,
            `on ${landing.path}`,
            [from !== 'direct' && `from ${from}`, landing.utm && `via ${landing.utm}`].filter(Boolean).join(' ') || 'direct visit',
            device,
            `${deviceInfo.type === 'Desktop' ? deviceData.displayInfo.viewportSize : deviceData.displayInfo.screenSize}`,
            browserInfo.language,
            `engaged ${Math.round(human.engagedAfterMs / 1000)}s`,
            `#${visitorId.slice(0, 8)}`,
        ].filter(Boolean).join(' · ').slice(0, 255);
    };

    // Save a first visit to Contentful; publishing it triggers the Slack alert
    const saveToContentful = async (visitorId, deviceData, human, botd, landing) => {
        const space = await managementClient.getSpace(CONTENTFUL_SPACE_ID);
        const environment = await space.getEnvironment('master');
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = new Date().toISOString();

        const deviceInfoText = `type: ${deviceData.deviceInfo.type} | model: ${deviceData.deviceInfo.model} | vendor: ${deviceData.deviceInfo.vendor} | platform: ${navigator.platform} | touchPoints: ${navigator.maxTouchPoints} | hardwareConcurrency: ${navigator.hardwareConcurrency}`;
        const displayInfoText = `screenSize: ${deviceData.displayInfo.screenSize} | viewportSize: ${deviceData.displayInfo.viewportSize} | colorDepth: ${deviceData.displayInfo.colorDepth} | pixelRatio: ${deviceData.displayInfo.pixelRatio} | orientation: ${window.screen?.orientation?.type || 'landscape-primary'}`;
        const browserInfoText = `name: ${deviceData.browserInfo.name} | version: ${deviceData.browserInfo.version} | language: ${deviceData.browserInfo.language} | cookiesEnabled: ${deviceData.browserInfo.cookiesEnabled} | userAgent: ${deviceData.browserInfo.userAgent}`;
        const systemInfoText = `name: ${deviceData.osInfo.name} | version: ${deviceData.osInfo.version}`;

        const visitorFields = {
            visitorId: { 'en-US': buildSummary(visitorId, deviceData, human, landing, timezone) },
            deviceInformation: { 'en-US': deviceInfoText },
            browserInformation: { 'en-US': browserInfoText },
            systemInformation: { 'en-US': systemInfoText },
            displayInformation: { 'en-US': displayInfoText },
            locationInformation: { 'en-US': `timezone: ${timezone} | landing page: ${window.location.origin}${landing.path} | visitorId: ${visitorId}` },
            firstVisit: { 'en-US': now },
            lastActive: { 'en-US': now },
            referrerInformation: { 'en-US': `referrer: ${landing.referrer || 'direct'} | utm: ${landing.utm || 'none'} | engagedAfter: ${human.engagedAfterMs}ms via ${human.interaction} | botCheck: isbot passed, BotD ${botd}` },
        };

        const entry = await environment.createEntry('visitor', { fields: visitorFields });
        const publishedEntry = await entry.publish();

        if (debug) {
            console.log('Contentful entry created:', publishedEntry);
        }

        return publishedEntry;
    };

  const updateVisitorData = async () => {
    if (!isTrackingEnabled() || updateInProgress.current || !mounted.current) {
        return;
    }

    try {
        updateInProgress.current = true;
        const visitorId = getOrCreateVisitorId();
        const deviceData = getEnhancedDeviceInfo();

        // Check if first visit (only first visits are reported to Contentful / Slack)
        const isFirstVisit = !localStorage.getItem('first_visit_date');

        // Send to GA4
        sendGAEvents(visitorId, deviceData);

        // Known crawlers identify themselves in the user agent
        if (!isFirstVisit || isbot(navigator.userAgent) || navigator.webdriver) {
            return;
        }

        // Captured now: by the time the visitor interacts they may have navigated
        const landing = {
            path: window.location.pathname,
            referrer: document.referrer,
            utm: getUtm(),
        };

        humanWait.current = new AbortController();
        const [human, botd] = await Promise.all([
            waitForHuman(humanWait.current.signal),
            detectAutomation(),
        ]);
        if (!human || !mounted.current || botd === 'bot') {
            return;
        }

        // Stored only once a person is confirmed, so someone who left without interacting is counted next time
        localStorage.setItem('first_visit_date', new Date().toISOString());
        await saveToContentful(visitorId, deviceData, human, botd, landing);
        console.log('Data successfully sent to Contentful:', visitorId);

        if (debug) {
            console.log('Visitor tracking completed successfully');
        }

    } catch (error) {
        console.error('Error in visitor tracking:', error);

        // Send error to GA4
        if (gaInitialized.current) {
            ReactGA4.event('tracking_error', {
                error_message: error.message,
                error_type: error.name,
                error_location: 'updateVisitorData'
            });
        }
    } finally {
        updateInProgress.current = false;
    }
};
    // Test function to send sample data
    const sendTestEvent = () => {
        if (!gaInitialized.current) {
            initializeGA();
        }

        const testData = {
            visitor_id: 'test-' + new Date().getTime(),
            device_type: 'Test Device',
            device_model: 'Test Model',
            browser_name: 'Test Browser',
            os_name: 'Test OS',
            screen_size: '1920x1080',
            referrer: 'Test Referrer',
            test_event: true
        };

        ReactGA4.event('visitor_data_test', testData);
        console.log('Test event sent to GA4:', testData);
    };

    // Component lifecycle
    useEffect(() => {
        mounted.current = true;
        
        if (isTrackingEnabled()) {
            initializeGA();
            updateVisitorData();
        }

        return () => {
            mounted.current = false;
            humanWait.current?.abort();
            updateInProgress.current = false;
        };
    }, []);

    // Route change tracking
    useEffect(() => {
        const handleRouteChange = () => {
            if (isTrackingEnabled() && gaInitialized.current) {
                ReactGA4.send({
                    hitType: "pageview",
                    page: window.location.pathname,
                    title: document.title
                });
            }
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, []);

    // Only render test button in development/debug mode
    if (process.env.NODE_ENV === 'development' || debug) {
        return (
            <div className="fixed bottom-4 right-4 z-50">
                <button
                    onClick={sendTestEvent}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow"
                >
                    Test GA4 Event
                </button>
            </div>
        );
    }

    return null;
};

export default VisitorTracker;